import { getGuestComponent } from '@apps/websiteCustomJsonComponents/config.guest';
import { JsonDataBlobComponentFragment, EventPageFragment } from '@graphql/generated';
import React, { Suspense, useEffect, useState } from 'react';
import type { GuestSiteComponentProps } from '@apps/websiteCustomJsonComponents/JsonComponent.types';
import { addError } from '@shared/utils/logger';

type GuestJsonComponentWrapperProps = {
  jsonComponentData: JsonDataBlobComponentFragment;
  pages?: readonly EventPageFragment[];
};

export const GuestSiteCustomJsonComponentWrapper = (props: GuestJsonComponentWrapperProps) => {
  const { jsonComponentData, pages } = props;
  const data = jsonComponentData.containerContent;
  const [Component, setComponent] = useState<React.ComponentType<GuestSiteComponentProps> | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const loadComponent = async () => {
      setIsLoading(true);
      try {
        const loadedComponent = await getGuestComponent(data?.schemaUrl);
        if (isMounted) {
          setComponent(() => loadedComponent);
        }
      } catch (error) {
        // Silently fail - component won't render if it fails to load
        addError(error, { context: 'GuestSiteCustomJsonComponentWrapper', operation: 'loadComponent', schemaUrl: data?.schemaUrl });
        if (isMounted) {
          setComponent(undefined);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadComponent();

    return () => {
      isMounted = false;
    };
  }, [data?.schemaUrl]);

  if (isLoading || !Component) {
    return null;
  }

  return (
    <Suspense fallback={null}>
      <Component
        {...props}
        data={data}
        pages={pages}
      />
    </Suspense>
  );
};
